import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePeriodicLoadData } from "../hooks/usePeriodicLoadData";
import { useNavigate } from "react-router-dom";
import * as utils from "../utils/utils";
import { canActAsPatient } from "../utils/utils";
import { fetchDependents } from "../apis/api.dependents";
import { queryDependentsKey } from "../apis/queryKeys";
import useActingPatient from "../hooks/useActingPatient";
import ActingPatientSelector from "./ActingPatientSelector";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
import { Calendar } from "../components/common/Calendar";
import { Textarea } from "../components/common/Textarea";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/common/Select";

import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";

import { getLocalizedDayName } from "../i18n/scheduleLabels";
import {
  generateTimeSlots,
  formatDate,
  formatClockTimeRange,
  formatDateDMY,
  getActiveSchedulesForDate,
  getScheduleConsultationMode,
  resolveAppointmentConsultationMode,
} from "../lib/ScheduleUtils";
import {
  ConsultationModeBadge,
  ConsultationModePicker,
} from "./ConsultationMode";
import {
  normalizePatientBookingHorizonDays,
  isDateWithinBookingHorizon,
} from "../lib/bookingHorizon";

import { CalendarDays, Clock, DollarSign, User, ArrowLeft, Mail, Phone, Stethoscope, Loader2, Info } from "lucide-react";
import customToast from "./common/Toaster";
import useBrandTranslation from "../hooks/useBrandTranslation";
import { acceptsInsurance, getAcceptedInsurances } from "../utils/healthInsurances";

// Códigos de país para teléfono (bandera + código) — mismo que SecretaryAssignAppointments
const PHONE_COUNTRY_CODES = [
  { code: "+54", label: "🇦🇷 +54", country: "Argentina" },
  { code: "+34", label: "🇪🇸 +34", country: "España" },
  { code: "+52", label: "🇲🇽 +52", country: "México" },
  { code: "+57", label: "🇨🇴 +57", country: "Colombia" },
  { code: "+56", label: "🇨🇱 +56", country: "Chile" },
  { code: "+598", label: "🇺🇾 +598", country: "Uruguay" },
  { code: "+51", label: "🇵🇪 +51", country: "Perú" },
  { code: "+593", label: "🇪🇨 +593", country: "Ecuador" },
  { code: "+591", label: "🇧🇴 +591", country: "Bolivia" },
  { code: "+595", label: "🇵🇾 +595", country: "Paraguay" },
  { code: "+55", label: "🇧🇷 +55", country: "Brasil" },
  { code: "+1", label: "🇺🇸 +1", country: "Estados Unidos" },
  { code: "+49", label: "🇩🇪 +49", country: "Alemania" },
  { code: "+33", label: "🇫🇷 +33", country: "Francia" },
  { code: "+39", label: "🇮🇹 +39", country: "Italia" },
  { code: "+44", label: "🇬🇧 +44", country: "Reino Unido" },
  { code: "+58", label: "🇻🇪 +58", country: "Venezuela" },
  { code: "+503", label: "🇸🇻 +503", country: "El Salvador" },
  { code: "+502", label: "🇬🇹 +502", country: "Guatemala" },
  { code: "+506", label: "🇨🇷 +506", country: "Costa Rica" },
  { code: "+507", label: "🇵🇦 +507", country: "Panamá" },
  { code: "+53", label: "🇨🇺 +53", country: "Cuba" },
  { code: "+972", label: "🇮🇱 +972", country: "Israel" },
  { code: "+86", label: "🇨🇳 +86", country: "China" },
  { code: "+81", label: "🇯🇵 +81", country: "Japón" },
  { code: "+91", label: "🇮🇳 +91", country: "India" },
];

function parseStoredPhone(phoneStr) {
  if (!phoneStr || typeof phoneStr !== "string") return { code: "+54", local: "" };
  const trimmed = phoneStr.trim();
  if (trimmed.startsWith("+")) {
    const match = trimmed.match(/^(\+\d{1,4})\s*(.*)$/);
    if (match) {
      const code = match[1];
      const local = (match[2] || "").trim();
      const known = PHONE_COUNTRY_CODES.find((c) => c.code === code);
      return { code: known ? code : "+54", local };
    }
  }
  return { code: "+54", local: trimmed };
}

export default function PatientBooking() {
  const { t } = useBrandTranslation();
  // ✅ No hook condicional: todo arriba
  const params = new URLSearchParams(window.location.search);
  const doctorId = params.get("doctorId"); // string
  const navigation = useNavigate();

  const { user } = useAuth();
  const {
    schedules,
    appointments,
    addAppointment,
    doctors,
    loadData,
    profiles,
    sharedSchedules,
    secretaries,
    getSchedulesByDoctor,
  } = useData();

  usePeriodicLoadData(loadData, undefined, !!user?.id && canActAsPatient(user));

  // Familiares del titular: puede reservar en su nombre. El sujeto se elige con
  // ActingPatientSelector (paciente activo global); acá se usa como sujeto del turno.
  const { data: dependents = [] } = useQuery({
    queryKey: queryDependentsKey(user?.id),
    queryFn: () => fetchDependents(user.id),
    enabled: !!user?.id && canActAsPatient(user),
  });

  const { acting, isSelf } = useActingPatient();

  const [selectedDate, setSelectedDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [patientName, setPatientName] = useState(user?.name || "");
  const parsedInitialPhone = parseStoredPhone(user?.phone || "");
  const [patientPhoneCountryCode, setPatientPhoneCountryCode] = useState(parsedInitialPhone.code);
  const [patientPhone, setPatientPhone] = useState(parsedInitialPhone.local);
  const [notes, setNotes] = useState("");
  // Evita reservas duplicadas por doble click / red lenta: el ref corta en el mismo tick,
  // el estado deshabilita el botón hasta tener la respuesta.
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modalidad elegida cuando el horario es "ambas" (presencial / virtual)
  const [chosenConsultationMode, setChosenConsultationMode] = useState(null);

  useEffect(() => {
    setChosenConsultationMode(null);
  }, [selectedSlot]);

  const consultationMode = resolveAppointmentConsultationMode(
    selectedSlot?.consultationMode,
    chosenConsultationMode
  );

  // Prellenar nombre/teléfono según el paciente activo (titular o familiar elegido).
  useEffect(() => {
    if (isSelf) {
      setPatientName(user?.name || "");
      const parsed = parseStoredPhone(user?.phone || "");
      setPatientPhoneCountryCode(parsed.code);
      setPatientPhone(parsed.local);
      return;
    }
    const dep = dependents.find((d) => d.id === acting.id);
    setPatientName(
      dep ? `${dep.name || ""} ${dep.last_name || ""}`.trim() : acting.name || ""
    );
    const parsed = parseStoredPhone(dep?.phone || "");
    setPatientPhoneCountryCode(parsed.code);
    setPatientPhone(parsed.local);
  }, [acting.id, acting.name, isSelf, dependents, user]);

  // ✅ Comparar por string para evitar mismatch number/string
  const doctor = useMemo(() => {
    if (!doctors || doctors.length === 0 || !doctorId) return undefined;
    return doctors.find((d) => String(d.id) === String(doctorId));
  }, [doctors, doctorId]);

  // Cobertura del paciente activo (titular o familiar). Si el profesional (o la
  // institución donde tiene agenda) no la atiende, solo se ofrecen sus horarios
  // particulares. Ver CLAUDE.md → "Cobertura médica y ubicación".
  const patientInsurance = useMemo(() => {
    const subject = isSelf
      ? (profiles || []).find((p) => p.id === user?.id) || user
      : dependents.find((d) => d.id === acting.id);
    return subject?.health_insurance_name?.trim() || "";
  }, [isSelf, profiles, user, dependents, acting.id]);

  const privateOnly = useMemo(() => {
    if (!doctor) return false;
    const institution = utils.findDoctorInstitution(doctor.id, {
      sharedSchedules,
      secretaries,
      getSchedulesByDoctor,
    });
    return !acceptsInsurance(
      getAcceptedInsurances(doctor, institution),
      patientInsurance
    );
  }, [doctor, sharedSchedules, secretaries, getSchedulesByDoctor, patientInsurance]);

  const getBookableSchedulesForDate = (date) => {
    const daySchedules = getActiveSchedulesForDate(schedules || [], doctorId, date);
    return privateOnly
      ? daySchedules.filter((s) => s.appointment_type === "private")
      : daySchedules;
  };

  // ✅ useMemo siempre declarado; internamente se protege si falta data
  const availableSlots = useMemo(() => {
    if (!doctorId || !schedules || !appointments) return [];

    const dateStr = formatDate(selectedDate);

    const daySchedules = getBookableSchedulesForDate(selectedDate);

    if (daySchedules.length === 0) return [];

    const dayAppointments = appointments.filter(
      (a) =>
        String(a.doctor_id) === String(doctorId) &&
        (a.date || "").substring(0, 10) === dateStr
    );

    const allSlots = daySchedules.flatMap((schedule) =>
      generateTimeSlots(
        schedule.start_time,
        schedule.end_time,
        schedule.slot_duration,
        schedule.appointment_type,
        dayAppointments,
        getScheduleConsultationMode(schedule)
      )
    );

    return allSlots
      .filter((slot) => slot.isAvailable)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [selectedDate, schedules, appointments, doctorId, privateOnly]);

  // ⛔️ No returns antes de declarar hooks. Recién acá los “guards” de render:
  if (!user || !canActAsPatient(user)) return null;

  if (!doctors || doctors.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">{t("app.booking.loadingDoctors", "Cargando médicos...")}</p>
      </div>
    );
  }

  if (!doctorId || !doctor) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">{t("app.booking.doctorNotFound", "Médico no encontrado")}</p>
      </div>
    );
  }

  const handleBookAppointment = async () => {
    if (!selectedSlot || !patientName.trim() || !notes.trim()) {
      customToast(t("app.appointments.fillRequired", "Por favor completa todos los campos requeridos (incluido el motivo)"), "warning");
      return;
    }
    if (!consultationMode) {
      customToast(t("schedule.consultationModeRequired", "Elegí si el turno es presencial o virtual"), "warning");
      return;
    }

    const fullPhone =
      patientPhone.trim() !== ""
        ? `${patientPhoneCountryCode} ${patientPhone.trim()}`
        : undefined;

    const appointment = {
      doctorId,
      patientId: isSelf ? user.id : acting.id,
      date: selectedDate,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      appointmentType: selectedSlot.appointmentType,
      consultationMode,
      status: "booked",
      patientName: patientName.trim(),
      patientPhone: fullPhone,
      notes: notes.trim() || undefined,
      price:
        selectedSlot.appointmentType === "private"
          ? doctor.private_appointment_price
          : undefined,
    };

    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const result = await addAppointment(appointment);
      if (result?.ok && result.appointment) {
        const created = result.appointment;
        if (selectedSlot.appointmentType === "private") {
          navigation("/payments/" + created.id);
        } else {
          customToast(t("app.appointments.bookSuccess", "Turno reservado exitosamente"), "success");
          setSelectedSlot(null);
          // El paciente activo se mantiene (puede reservar varios para el mismo);
          // nombre/teléfono quedan sincronizados por el efecto de arriba.
        }
      } else if (!result?.userNotified) {
        customToast(t("app.appointments.bookError", "Error al reservar el turno. Intenta nuevamente."), "danger");
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const bookingHorizonDays = normalizePatientBookingHorizonDays(
    doctor?.patient_booking_horizon_days
  );

  const isDateDisabled = (date) => {
    if (!isDateWithinBookingHorizon(date, bookingHorizonDays)) return true;
    // Sin cobertura atendida: solo los días en que atiende particular.
    return privateOnly && getBookableSchedulesForDate(date).length === 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-32">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Reservar Turno
          </h1>
          <p className="text-gray-600">
            Selecciona una fecha y horario disponible con {doctor.name}
          </p>
        </div>

        {privateOnly && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {patientInsurance
                ? t(
                    "app.booking.privateOnlyInsurance",
                    "Este profesional no atiende por {{insurance}}: solo se muestran los días y horarios en que atiende particular.",
                    { insurance: patientInsurance }
                  )
                : t(
                    "app.booking.privateOnlyNoInsurance",
                    "No tenés una cobertura médica cargada: solo se muestran los días y horarios en que el profesional atiende particular."
                  )}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          {/* Calendario */}
          <Card className="">
            <CardHeader>
              <CardTitle>{t("app.appointments.selectDate", "Seleccionar Fecha")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                disabled={isDateDisabled}
                className="rounded-md border"
              />
            </CardContent>
          </Card>

          {/* Horarios disponibles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {getLocalizedDayName(selectedDate.getDay())}{" "}
                {formatDateDMY(selectedDate)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {availableSlots.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No hay horarios disponibles para esta fecha
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {availableSlots.map((slot, index) => (
                    <Button
                      key={`${slot.startTime}-${slot.endTime}-${index}`}
                      variant={selectedSlot === slot ? "default" : "outline"}
                      className="w-full justify-between h-auto p-3"
                      onClick={() => setSelectedSlot(slot)}
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>
                          {formatClockTimeRange(slot.startTime, slot.endTime)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            slot.appointmentType === "private"
                              ? "appointmentPrivate"
                              : "appointmentInsurance"
                          }
                        >
                          {slot.appointmentType === "private"
                            ? "Particular"
                            : "Obra Social"}
                        </Badge>
                        <ConsultationModeBadge mode={slot.consultationMode} />
                        {slot.appointmentType === "private" && (
                          <div className="flex items-center gap-1 text-sm">
                            <DollarSign className="w-3 h-3" />
                            {doctor.private_appointment_price}
                          </div>
                        )}
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Formulario de reserva */}
          <Card>
            <CardHeader>
              <CardTitle>{t("app.appointments.patientData", "Datos del Paciente")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Datos del profesional */}
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">
                  Profesional
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">
                      {doctor.name} {doctor.last_name || ""}
                    </span>
                  </div>
                  {doctor.specialization && (
                    <div className="flex items-center gap-2">
                      <Stethoscope className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-700">{doctor.specialization}</span>
                    </div>
                  )}
                  {doctor.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-700">{doctor.phone}</span>
                    </div>
                  )}
                  {doctor.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-700">{doctor.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedSlot && (
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-medium mb-2">{t("app.booking.selectedSlot", "Turno Seleccionado:")}</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      <span>{formatDateDMY(selectedDate)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>
                        {formatClockTimeRange(selectedSlot.startTime, selectedSlot.endTime)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          selectedSlot.appointmentType === "private"
                            ? "appointmentPrivate"
                            : "appointmentInsurance"
                        }
                      >
                        {selectedSlot.appointmentType === "private"
                          ? "Particular"
                          : "Obra Social"}
                      </Badge>
                      {selectedSlot.appointmentType === "private" && (
                        <span className="font-medium text-green-600">
                          ${doctor.private_appointment_price}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {selectedSlot && (
                <ConsultationModePicker
                  slotMode={selectedSlot.consultationMode}
                  value={chosenConsultationMode}
                  onChange={setChosenConsultationMode}
                />
              )}

              <div>
                <Label>{t("app.booking.bookingFor", "¿Para quién es el turno?")}</Label>
                <div className="mt-2">
                  <ActingPatientSelector />
                </div>
              </div>

              <div>
                <Label htmlFor="patientName">{t("app.booking.fullName", "Nombre Completo *")}</Label>
                <Input
                  id="patientName"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  placeholder={t("app.booking.fullNamePlaceholder", "Ingresa tu nombre completo")}
                />
              </div>

              <div>
                <Label htmlFor="patientPhone">{t("app.booking.phone", "Teléfono")}</Label>
                <div className="flex gap-2 items-center justify-center">
                  <Select
                    value={patientPhoneCountryCode}
                    onValueChange={(value) => setPatientPhoneCountryCode(value)}
                  >
                    <SelectTrigger className="w-[130px] shrink-0 h-[48px] mt-2">
                      <SelectValue placeholder={t("app.booking.countryCode", "Código")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PHONE_COUNTRY_CODES.map(({ code, label }) => (
                        <SelectItem key={code} value={code}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="patientPhone"
                    value={patientPhone}
                    onChange={(e) => setPatientPhone(e.target.value)}
                    placeholder={t("app.booking.phonePlaceholder", "Ej: 11 1234-5678")}
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">{t("app.booking.reason", "Motivo *")}</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("app.booking.reasonPlaceholder", "Motivo de consulta, observaciones, etc.")}
                  rows={3}
                />
              </div>

              <Button
                onClick={handleBookAppointment}
                disabled={
                  !selectedSlot ||
                  !patientName.trim() ||
                  !notes.trim() ||
                  !consultationMode ||
                  isSubmitting
                }
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("app.appointments.booking", "Reservando turno…")}
                  </>
                ) : selectedSlot?.appointmentType === "private"
                  ? "Reservar y Pagar"
                  : "Reservar Turno"}
              </Button>

              {selectedSlot?.appointmentType === "private" && (
                <p className="text-sm text-gray-600 text-center">
                  Serás redirigido a la página de pagos para completar la
                  reserva
                </p>
              )}
              <div className="h-6 md:h-0" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
